import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { SubscriptionPaymentMethodValues } from '@/api/generated/enums';
import { useNotify } from '@/api/notify';
import { DatePicker } from '@/components/DatePicker';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { controlClass, Field, selectClass } from '@/components/Field';
import { Button, Card, Notice, SectionTitle } from '@/components/PageHeading';
import { formatDate, toDayjs, toIsoDate } from '@core/format/dates';

import { platformKeys, recordPayment, useTenantBilling } from './api';
import {
  coverOf,
  kindLabels,
  kindsFor,
  methodLabels,
  paymentDraftFrom,
  paymentProblems,
  paymentRequest,
  withKind,
  type PaymentDraft,
  todayIso,
} from './billing';

/**
 * One sum received, or one stretch of days given (docs/Platform_Billing_Design.md §5), as a page.
 * It opens on the server's suggestion — the kind due next, its amount, and the stretch from the day
 * after the last one paid for — so the ordinary renewal is a check and a save. Saving numbers the
 * receipt and opens it to print.
 */
export function PaymentPage({
  tenantId,
  onBack,
  onRecorded,
}: {
  tenantId: number;
  onBack: () => void;
  onRecorded: (paymentId: number) => void;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const billing = useTenantBilling(tenantId);
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  const [draft, setDraft] = useState<PaymentDraft | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (billing.data && !draft) {
      setDraft(
        paymentDraftFrom(billing.data.suggested, billing.data.terms?.chargingModel, todayIso()),
      );
    }
  }, [billing.data, draft]);

  if (!draft || !billing.data) return null;

  const kinds = kindsFor(billing.data.terms?.chargingModel);
  const free = draft.kind === 'Complimentary';
  const cover = coverOf(draft.kind);
  const problems = paymentProblems(draft, todayIso());

  const set =
    <K extends keyof PaymentDraft>(key: K) =>
    (value: PaymentDraft[K]) =>
      setDraft((d) => (d ? { ...d, [key]: value } : d));

  const save = async () => {
    setBusy(true);
    try {
      const receipt = await recordPayment(tenantId, paymentRequest(draft));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: platformKeys.billing(tenantId) }),
        queryClient.invalidateQueries({ queryKey: platformKeys.companies }),
      ]);
      notify.success(`${receipt.receiptNumber} recorded.`);
      onRecorded(receipt.id);
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(false);
    }
  };

  const date = (key: 'receivedOn' | 'coversFrom' | 'coversTo', label: string, hint?: string) => (
    <Field label={label} hint={hint} required={key === 'receivedOn' || cover === 'required'}>
      <DatePicker
        value={toDayjs(draft[key]) ?? null}
        onChange={(value) => set(key)(toIsoDate(value) ?? '')}
        className="w-full"
      />
    </Field>
  );

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to the company"
          className="text-default flex size-8 cursor-pointer items-center justify-center rounded hover:bg-light hover:text-gray-900"
        >
          <i className="icon icon-arrow-left" />
        </button>
        <div>
          <h1 className="page-title">Record payment</h1>
          <p className="page-subtitle">
            {billing.data.tenantName}
            {billing.data.paidUntil ? ` · paid until ${formatDate(billing.data.paidUntil)}` : ''}
          </p>
        </div>
      </div>

      <div ref={fields} {...enterKeyScope} className="grid grid-cols-12 gap-4">
        <div className="col-span-12 flex flex-col gap-4 xl:col-span-8">
          <Card>
            <SectionTitle icon="icon icon-receipt" hue="teal">
              The payment
            </SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="For" required>
                <select
                  className={selectClass}
                  value={draft.kind}
                  onChange={(event) =>
                    setDraft((d) =>
                      d ? withKind(d, event.target.value as PaymentDraft['kind']) : d,
                    )
                  }
                >
                  {kinds.map((kind) => (
                    <option key={kind} value={kind}>
                      {kindLabels[kind]}
                    </option>
                  ))}
                </select>
              </Field>
              {date('receivedOn', free ? 'Given on' : 'Received on')}
              {!free && (
                <>
                  <Field label="Amount" required>
                    <div className="relative">
                      <span className="text-default pointer-events-none absolute inset-y-0 start-3 flex items-center">
                        ₹
                      </span>
                      <input
                        className={`${controlClass} ps-7 text-end`}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="any"
                        value={draft.amount}
                        onChange={(event) => set('amount')(event.target.value)}
                        onWheel={(event) => event.currentTarget.blur()}
                      />
                    </div>
                  </Field>
                  <Field label="How it arrived" required>
                    <select
                      className={selectClass}
                      value={draft.method}
                      onChange={(event) =>
                        set('method')(event.target.value as PaymentDraft['method'])
                      }
                    >
                      {SubscriptionPaymentMethodValues.map((method) => (
                        <option key={method} value={method}>
                          {methodLabels[method]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field
                    label="Reference"
                    hint="UTR or cheque number — what finds it in a statement."
                  >
                    <input
                      className={`${controlClass} font-mono`}
                      type="text"
                      maxLength={64}
                      value={draft.reference}
                      onChange={(event) => set('reference')(event.target.value)}
                    />
                  </Field>
                </>
              )}
            </div>
          </Card>

          {cover !== 'none' && (
            <Card>
              <SectionTitle icon="icon icon-calendar-range" hue="indigo">
                {free ? 'The days given' : 'What it pays for'}
              </SectionTitle>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {date('coversFrom', 'From', 'The first day it pays for.')}
                {date('coversTo', 'To', 'The last day, inclusive.')}
              </div>
              {cover === 'optional' && (
                <p className="text-default mt-3 text-[length:var(--scale-field-hint)]">
                  A licence usually covers the first year. Clear both dates if this one covers none
                  and the AMC starts at once.
                </p>
              )}
            </Card>
          )}

          <Card>
            <Field
              label="Note"
              hint={free ? 'Why these days are given free.' : 'Anything to keep with it.'}
              required={free}
            >
              <input
                className={controlClass}
                type="text"
                maxLength={512}
                value={draft.note}
                onChange={(event) => set('note')(event.target.value)}
              />
            </Field>
          </Card>
        </div>

        <div className="col-span-12 flex flex-col gap-4 xl:col-span-4">
          {problems.length > 0 && (
            <Notice tone="warning" title="Before saving">
              <ul className="ps-0">
                {problems.map((p) => (
                  <li key={p} className="list-none">
                    {p}
                  </li>
                ))}
              </ul>
            </Notice>
          )}
          <Card>
            <p className="text-default mb-3 text-[length:var(--scale-field-hint)]">
              Saving gives it the next receipt number and opens the receipt to print. A mistake is
              voided afterwards, never edited.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                variant="primary"
                icon="icon icon-check"
                data-enter-key-commit
                shortcut="save"
                loading={busy}
                disabled={busy || problems.length > 0}
                onClick={() => void save()}
              >
                {busy ? 'Recording…' : free ? 'Record days given' : 'Record payment'}
              </Button>
              <Button disabled={busy} shortcut="back" onClick={onBack}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
